import { ArrowLeft, BookOpen, Calendar, User, Brain, AlertTriangle, Activity, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

export const Book = () => {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-clarity-bg relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50"></div>
      
      {/* Ambient glow - hidden on mobile */}
      <div className="hidden md:block absolute top-1/4 right-1/4 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl"></div>
      <div className="hidden md:block absolute bottom-1/3 left-1/4 w-80 h-80 bg-brand-tertiary/10 rounded-full blur-3xl"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 md:p-8 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <Button 
            onClick={() => navigate("/login")}
            data-testid="book-page-login-btn"
            className="bg-brand-primary text-clarity-bg font-semibold px-6 py-2 rounded-md hover:bg-yellow-400 hover:shadow-[0_0_15px_rgba(229,184,76,0.4)] transition-colors"
          >
            Sign In
          </Button>
        </header>

        {/* Main Content */}
        <main className="px-6 md:px-8 lg:px-16 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            
            {/* Hero Section */}
            <div className="flex flex-col lg:flex-row gap-10 items-center mb-16">
              {/* Book Cover */}
              <div className="flex-shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3a2327a1-55b4-4a12-9183-41431867dc72/artifacts/je18pdiq_ClarityOS%20Final%20Book%20Cover%20Feb%2021st.png"
                  alt="ClarityOS Book Cover"
                  className="w-64 md:w-80 rounded-xl shadow-2xl shadow-brand-primary/30"
                  data-testid="book-cover-large"
                />
              </div>
              
              {/* Book Info */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-primary/10 border border-brand-primary/30 mb-6">
                  <BookOpen className="w-4 h-4 text-brand-primary" />
                  <span className="text-sm text-brand-primary font-medium">Coming Soon</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl mb-4" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                  <span className="font-bold text-white">CLARITY</span>
                  <span className="font-normal text-brand-primary">OS</span>
                </h1>
                <p className="text-xl md:text-2xl text-brand-primary font-medium mb-6">
                  The 15-Minute Protocol to Shift from Rigid to Fluid Leadership
                </p>
                
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a1a6583a-1055-4d0b-8aac-c86cbed6671f/artifacts/qmgj6mpw_Debu%20Formal.png"
                    alt="Debu Mishra"
                    className="w-14 h-14 rounded-full object-cover border-2 border-brand-primary/30"
                  />
                  <div className="text-left">
                    <p className="text-white font-semibold">Debu Mishra</p>
                    <p className="text-slate-500 text-sm">Leadership Advisor & Creator of ClarityOS</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center lg:justify-start gap-2 text-brand-tertiary text-lg mb-8">
                  <Calendar className="w-5 h-5" />
                  <span className="font-medium">Releasing March 15, 2026</span>
                </div>

                <Button 
                  onClick={() => navigate("/login")}
                  className="bg-brand-primary text-clarity-bg font-bold text-lg px-8 py-4 rounded-md hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(229,184,76,0.5)] transition-colors"
                  data-testid="start-protocol-btn"
                >
                  Start the Protocol Now
                </Button>
              </div>
            </div>

            {/* The Book That Challenges Section */}
            <div className="glass rounded-2xl p-8 md:p-10 mb-12 border border-brand-primary/20">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
                The Book That Challenges the $366 Billion Leadership Development Industry
              </h2>
              <div className="space-y-4 text-slate-300 text-lg leading-relaxed">
                <p>
                  Every leadership model-transformational, servant, authentic, adaptive-assumes your brain is working as designed.
                </p>
                <p className="text-brand-primary font-medium">
                  What if it's not?
                </p>
                <p>
                  This book reveals why 60-80% of executives operate from their amygdala under pressure-and what to do about it. 
                  <span className="text-white font-medium"> ClarityOS</span> is the neuroscience-backed protocol that trains your nervous system 
                  to shift from reactive to responsive leadership in 15 minutes a day.
                </p>
              </div>
              
              {/* Core Philosophy */}
              <div className="mt-8 p-6 rounded-xl bg-brand-tertiary/10 border border-brand-tertiary/20">
                <p className="text-xl text-center">
                  <span className="text-slate-400">Leadership isn't downstream of your experience.</span>
                  <br />
                  <span className="text-white font-semibold">It's downstream of your Theta/Beta Ratio.</span>
                </p>
              </div>
            </div>

            {/* OS Analogy Section */}
            <div className="text-center mb-16">
              <p className="text-slate-400 text-lg mb-2">Your Leadership Has an Operating System.</p>
              <p className="text-slate-500 mb-2">Most Leaders Try to Upgrade the Apps.</p>
              <p className="text-2xl md:text-3xl font-bold">
                <span className="text-white">ClarityOS Upgrades the </span>
                <span className="text-brand-primary">OS.</span>
              </p>
            </div>

            {/* What's Inside the Book - 3 Parts */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-white mb-8">What's Inside the Book</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Part 1 */}
                <div className="glass rounded-xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">Part 1: The Problem</h3>
                  <p className="text-slate-500 text-sm mb-4">Why Leadership Development Fails</p>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li>• The Substrate Fallacy</li>
                    <li>• Amygdala Hijack Under Pressure</li>
                    <li>• Why Coaching Hits a Ceiling</li>
                    <li>• The Workshop Trap</li>
                    <li>• The Missing Layer in Frameworks</li>
                  </ul>
                </div>

                {/* Part 2 */}
                <div className="glass rounded-xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-brand-tertiary/10 flex items-center justify-center mb-4">
                    <Brain className="w-6 h-6 text-brand-tertiary" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">Part 2: The Science</h3>
                  <p className="text-slate-500 text-sm mb-4">The Neuroscience Behind State Shifting</p>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li>• The 3-Neurochemical Model</li>
                    <li>• EEG Validation (173 Days of Data)</li>
                    <li>• Theta/Beta Ratio Explained</li>
                    <li>• The Fluidity Quotient Framework</li>
                    <li>• Neuroplasticity vs. Psychology</li>
                    <li>• State Contagion in Teams</li>
                  </ul>
                </div>

                {/* Part 3 */}
                <div className="glass rounded-xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-brand-primary/10 flex items-center justify-center mb-4">
                    <Activity className="w-6 h-6 text-brand-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">Part 3: The Protocol</h3>
                  <p className="text-slate-500 text-sm mb-4">The 15-Minute Daily Practice</p>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li>• Phase 1: RESET (Breathwork)</li>
                    <li>• Phase 2: LOCK (Focused Gazing)</li>
                    <li>• Phase 3: INSTALL (Deep Rest)</li>
                    <li>• The Firewall Protocol (3-min reset)</li>
                    <li>• Measurement & Tracking</li>
                    <li>• 30-Day Transformation Roadmap</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* About Debu Mishra */}
            <div className="glass rounded-2xl p-8 md:p-10 mb-12">
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a1a6583a-1055-4d0b-8aac-c86cbed6671f/artifacts/qmgj6mpw_Debu%20Formal.png"
                  alt="Debu Mishra"
                  className="w-32 h-32 rounded-xl object-cover border-2 border-brand-primary/30 mx-auto md:mx-0"
                />
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-2 text-center md:text-left">
                    About Debu Mishra
                  </h2>
                  <p className="text-brand-primary font-medium text-center md:text-left">Leadership Advisor & Creator of ClarityOS</p>
                </div>
              </div>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  <span className="text-white font-medium">Debu Mishra</span> is a leadership advisor, executive coach, and former Consulting Partner/Leader at leading management consulting firms - Deloitte, PwC, Arthur Andersen, Hay Group and Hewitt Associates.
                </p>
                <p>
                  He has over 35 years of experience helping CEOs and boards build high-performing teams. Debu has advised Fortune 100 companies, Government & Ministries; high-growth startups and unicorns; Private Equity and Sovereign Wealth Funds; spread across Middle East, India, Africa, China, UK and USA.
                </p>
                <p>
                  In this work he often noticed leaders change for a while and then relapse into their old self at the first sign of a stressful situation. Debu started looking into patterns and saw that almost all leadership frameworks and systems propagated 'archetypes' of leadership as the foundation. Leaders also see themselves as a particular archetype and that is what becomes difficult to move away from.
                </p>
                <p>
                  His own exploration of neuroscience was telling him a very different story – the brain is an amazing organ and has inherent plasticity through which it can keep changing throughout life (neuroplasticity). He asked himself - <span className="text-white italic">What if I could increase my neuroplasticity? Will it give me a more versatile ability to be effective in different situations? Is there evidence that this works?</span>
                </p>
                <p>
                  He looked at ancient protocols that were aimed at a healthy brain. He put them into practice and started taking EEG measurements of his brain over a period of time. The results were amazing. It enhanced his neuroplasticity. It changed him as well.
                </p>
                <p>
                  This book is a distillation of the protocol for enhancing neuroplasticity. It is from the powerful convergence of neuroscience and Vedanta – yoga and tantra. The application is for the noisy world of today.
                </p>
                <p className="text-brand-primary font-medium">
                  ClarityOS - a neuroscience-backed protocol that measures and trains the nervous system.
                </p>
                <p className="text-slate-500 italic">
                  Based in Dubai. Lives with his spouse and two daughters. He practices the protocol daily.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <h2 className="font-heading text-3xl font-bold text-white mb-4">
                Don't Wait for the Book
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
                Start experiencing the benefits of the ClarityOS protocol today. 
                The app guides you through each phase with timers, progress tracking, and the 3-minute Firewall Protocol for real-time state shifting.
              </p>
              <Button 
                onClick={() => navigate("/login")}
                className="bg-brand-primary text-clarity-bg font-bold text-lg px-10 py-4 rounded-md hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(229,184,76,0.5)] transition-colors group"
                data-testid="cta-start-protocol"
              >
                Start Your Protocol
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform inline" />
              </Button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="px-6 md:px-8 py-8 border-t border-white/5 mt-16">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2026 ClarityOS. Optimize your neural operating system.
            </p>
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_clarity-wellness-log/artifacts/dv3nbk4x_Logo%20new%20copy.jpg" 
                alt="ClarityOS" 
                className="w-8 h-8 rounded-lg object-cover"
              />
              <span className="text-lg tracking-tight" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                <span className="font-bold text-white">CLARITY</span>
                <span className="font-normal text-brand-primary">OS</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
